import type {
  GraphMember,
} from '@/contexts/FamilyGraphContext';

import {
  computeTreeBranchPaths,
} from '@/lib/treeBranchPathAuthority';

import {
  projectCanonicalTreeCompatibilityEdges,
} from './CanonicalTreeCompatibilityEdgeProjection';

import {
  runCanonicalTreeProjectionParityGate,
  type CanonicalTreeProjectionMigrationReadiness,
} from './CanonicalTreeProjectionParityGate';

import type {
  CanonicalViewerBranch,
  CanonicalViewerMemberProjection,
} from './CanonicalTreeViewerProjection';

export type CanonicalBranchConsumerSource =
  | 'canonical'
  | 'legacy-fallback';

export interface CanonicalTreeBranchConsumerResult {
  readonly branchPathByMemberId:
    ReadonlyMap<string, string>;

  readonly source:
    CanonicalBranchConsumerSource;

  readonly migrationReadiness:
    CanonicalTreeProjectionMigrationReadiness;

  readonly legacyBranchPathByMemberId:
    ReadonlyMap<string, string>;

  readonly canonicalBranchPathByMemberId:
    ReadonlyMap<string, string>;

  readonly canonicalAdditionCount: number;
  readonly unresolvedEvidenceCount: number;
  readonly conflictingEvidenceCount: number;
}

export interface CanonicalTreeBranchConsumerInput {
  readonly members:
    readonly GraphMember[];

  readonly viewerId:
    string | null | undefined;
}

const canonicalBranchRoot = (
  branch: CanonicalViewerBranch,
): string => {
  switch (branch) {
    case 'self':
      return 'self';

    case 'maternal':
      return 'maternal.ancestor';

    case 'paternal':
      return 'paternal.ancestor';

    case 'both':
      return 'both.ancestor';

    case 'descendant':
      return 'descendant.member';

    case 'partner-affiliated':
      return 'partner-affiliated.member';

    case 'child-mediated':
      return 'child-mediated.member';

    case 'affiliated':
      return 'affiliated.member';

    case 'unresolved':
    default:
      return 'unresolved.member';
  }
};

const canonicalProjectionPath = (
  projection:
    CanonicalViewerMemberProjection,
): string => {
  if (projection.branch === 'self') {
    return 'self';
  }

  const root =
    canonicalBranchRoot(
      projection.branch,
    );

  const edgePath =
    projection.sourceEdgeIds.length > 0
      ? projection.sourceEdgeIds.join('.')
      : projection.memberId;

  return `${root}.${edgePath}`;
};

const freezeMap = (
  values: ReadonlyMap<string, string>,
): ReadonlyMap<string, string> =>
  new Map(values);

export const resolveCanonicalTreeBranchConsumer = (
  input: CanonicalTreeBranchConsumerInput,
): CanonicalTreeBranchConsumerResult => {
  const members =
    [...input.members];

  const legacyBranchPathByMemberId =
    computeTreeBranchPaths(
      members,
      input.viewerId,
    );

  if (
    !input.viewerId ||
    !members.some(
      member =>
        member.id === input.viewerId,
    )
  ) {
    return Object.freeze({
      branchPathByMemberId:
        freezeMap(
          legacyBranchPathByMemberId,
        ),

      source:
        'legacy-fallback',

      migrationReadiness:
        'BLOCKED',

      legacyBranchPathByMemberId:
        freezeMap(
          legacyBranchPathByMemberId,
        ),

      canonicalBranchPathByMemberId:
        new Map(),

      canonicalAdditionCount:
        0,

      unresolvedEvidenceCount:
        1,

      conflictingEvidenceCount:
        0,
    });
  }

  const compatibility =
    projectCanonicalTreeCompatibilityEdges({
      members,
    });

  const parity =
    runCanonicalTreeProjectionParityGate({
      viewerId:
        input.viewerId,
      members,
      edges:
        compatibility.edges,
    });

  const canonicalBranchPathByMemberId =
    new Map<string, string>();

  for (
    const [
      memberId,
      projection,
    ] of parity.canonical.projections
  ) {
    canonicalBranchPathByMemberId.set(
      memberId,
      canonicalProjectionPath(
        projection,
      ),
    );
  }

  const parityReady =
    parity.migrationReadiness === 'READY' ||
    parity.migrationReadiness ===
      'READY-WITH-CANONICAL-ADDITIONS';

  const compatibilityReady =
    compatibility.unresolvedEvidence.length === 0 &&
    compatibility.invalidEvidence.length === 0;

  const canonicalReady =
    parityReady &&
    compatibilityReady &&
    parity.canonical
      .unresolvedEvidence.length === 0 &&
    parity.canonical
      .conflictingEvidence.length === 0;

  const selected =
    canonicalReady
      ? canonicalBranchPathByMemberId
      : legacyBranchPathByMemberId;

  return Object.freeze({
    branchPathByMemberId:
      freezeMap(selected),

    source:
      canonicalReady
        ? 'canonical'
        : 'legacy-fallback',

    migrationReadiness:
      canonicalReady
        ? parity.migrationReadiness
        : 'BLOCKED',

    legacyBranchPathByMemberId:
      freezeMap(
        legacyBranchPathByMemberId,
      ),

    canonicalBranchPathByMemberId:
      freezeMap(
        canonicalBranchPathByMemberId,
      ),

    canonicalAdditionCount:
      parity.summary
        .canonicalAdditionCount,

    unresolvedEvidenceCount:
      compatibility
        .unresolvedEvidence.length +
      compatibility
        .invalidEvidence.length +
      parity.canonical
        .unresolvedEvidence.length,

    conflictingEvidenceCount:
      parity.canonical
        .conflictingEvidence.length,
  });
};
